import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileCheck, AlertTriangle, CheckCircle2, Clock, TrendingUp,
  Package, Truck, AlertCircle, Sparkles, ArrowRight, ActivityIcon,
  Calendar, ChevronDown, ShieldCheck, TriangleAlert,
  ArrowUpRight, BarChart3, ListChecks, FileText, ShieldAlert,
} from 'lucide-react';
import AIInsightCard from '@/components/shipment/AIInsightCard';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getMonthsAgoForInput, getTodayForInput } from '@/utils/dateUtils';

// ─── Icon Map ─────────────────────────────────────────────────────────────────
// Add any new icons here when extending DASHBOARD_DATA
const ICON_MAP = {
  AlertTriangle, CheckCircle2, Clock, TrendingUp,
  Package, Truck, AlertCircle, ActivityIcon,
  BarChart3, ListChecks, FileText, ShieldAlert, ShieldCheck,
};

// ─── Dashboard Data ───────────────────────────────────────────────────────────
// All static data lives here. To connect to an API, fetch your response and
// replace `DASHBOARD_DATA` with the parsed payload — no other change needed.
const DASHBOARD_DATA = {

  kpis: [
    { iconKey: 'AlertTriangle', label: 'Pending Clearance',   value: '8',      sub: 'awaiting clearance', trend: '-4%',  trendUp: false, accent: 'orange'  },
    { iconKey: 'CheckCircle2',  label: 'Cleared Today',       value: '24',     sub: 'shipments cleared',  trend: '+12%', trendUp: true,  accent: 'emerald' },
    { iconKey: 'Clock',         label: 'Avg Processing Time', value: '15 min', sub: 'per filing',         trend: '-8%',  trendUp: false, accent: 'blue'    },
    { iconKey: 'ShieldAlert',   label: 'Issues / Delays',     value: '3',      sub: 'needs review',       trend: '+2',   trendUp: true,  accent: 'rose'    },
  ],

  summary: [
    { iconKey: 'FileText',     label: 'Total Filings',       value: '40',     tone: 'ink',     hint: 'this period'   },
    { iconKey: 'Clock',        label: 'Pending',             value: '13',     tone: 'orange',  hint: 'awaiting docs' },
    { iconKey: 'CheckCircle2', label: 'Approved',            value: '24',     tone: 'emerald', hint: 'cleared'       },
    { iconKey: 'TrendingUp',   label: 'Avg Processing Time', value: '15 min', tone: 'blue',    hint: 'per filing'    },
  ],

  countries: [
    { name: 'Netherlands', hours: 48 },
    { name: 'India',       hours: 72 },
    { name: 'USA',         hours: 32 },
    { name: 'Germany',     hours: 45 },
    { name: 'Singapore',   hours: 22 },
    { name: 'China',       hours: 56 },
  ],

  filings: [
    { id: 'CONT1026', port: 'Mumbai',      country: 'UAE',         date: '6 Feb 2026',  doc: 'missing',  docLabel: 'Missing Documents', clear: '-',   status: 'pending'   },
    { id: 'CONT1033', port: 'Singapore',   country: 'Singapore',   date: '13 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '38h', status: 'approved'  },
    { id: 'CONT1028', port: 'Singapore',   country: 'Singapore',   date: '9 Feb 2026',  doc: 'missing',  docLabel: 'Missing Documents', clear: '-',   status: 'pending'   },
    { id: 'CONT1011', port: 'Rotterdam',   country: 'India',       date: '10 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '-',   status: 'pending'   },
    { id: 'CONT1009', port: 'Singapore',   country: 'Netherlands', date: '6 Feb 2026',  doc: 'missing',  docLabel: 'Missing Documents', clear: '-',   status: 'delayed'   },
    { id: 'CONT1031', port: 'Hamburg',     country: 'USA',         date: '10 Feb 2026', doc: 'review',   docLabel: 'Under Review',      clear: '-',   status: 'submitted' },
    { id: 'CONT1015', port: 'Singapore',   country: 'China',       date: '13 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '57h', status: 'approved'  },
    { id: 'CONT1018', port: 'Los Angeles', country: 'USA',         date: '18 Feb 2026', doc: 'review',   docLabel: 'Under Review',      clear: '-',   status: 'submitted' },
    { id: 'CONT1021', port: 'Singapore',   country: 'India',       date: '13 Feb 2026', doc: 'review',   docLabel: 'Under Review',      clear: '-',   status: 'delayed'   },
    { id: 'CONT1019', port: 'Shanghai',    country: 'China',       date: '11 Feb 2026', doc: 'missing',  docLabel: 'Missing Documents', clear: '-',   status: 'delayed'   },
    { id: 'CONT1039', port: 'Mumbai',      country: 'Singapore',   date: '15 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '-',   status: 'submitted' },
    { id: 'CONT1020', port: 'Hamburg',     country: 'USA',         date: '14 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '56h', status: 'approved'  },
    { id: 'CONT1005', port: 'Mumbai',      country: 'UAE',         date: '5 Feb 2026',  doc: 'review',   docLabel: 'Under Review',      clear: '-',   status: 'pending'   },
    { id: 'CONT1034', port: 'Los Angeles', country: 'Netherlands', date: '6 Feb 2026',  doc: 'complete', docLabel: 'Complete',          clear: '-',   status: 'delayed'   },
    { id: 'CONT1025', port: 'Mumbai',      country: 'USA',         date: '9 Feb 2026',  doc: 'complete', docLabel: 'Complete',          clear: '-',   status: 'pending'   },
    { id: 'CONT1013', port: 'Hamburg',     country: 'India',       date: '17 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '-',   status: 'pending'   },
    { id: 'CONT1023', port: 'Dubai',       country: 'USA',         date: '4 Feb 2026',  doc: 'missing',  docLabel: 'Missing Documents', clear: '-',   status: 'delayed'   },
    { id: 'CONT1029', port: 'Shanghai',    country: 'USA',         date: '14 Feb 2026', doc: 'missing',  docLabel: 'Missing Documents', clear: '22h', status: 'approved'  },
    { id: 'CONT1014', port: 'Mumbai',      country: 'Singapore',   date: '6 Feb 2026',  doc: 'review',   docLabel: 'Under Review',      clear: '-',   status: 'pending'   },
    { id: 'CONT1002', port: 'Mumbai',      country: 'Germany',     date: '14 Feb 2026', doc: 'complete', docLabel: 'Complete',          clear: '16h', status: 'approved'  },
  ],

  recent: [
    { id: 'CF-2025-045', shipment: 'MSCU1234567', port: 'Los Angeles', status: 'cleared',    clear: '1.8 hrs', doc: 'complete', docLabel: 'Complete'     },
    { id: 'CF-2025-046', shipment: 'MAEU9876543', port: 'New York',    status: 'pending',    clear: '-',       doc: 'awaiting', docLabel: 'Awaiting BOL' },
    { id: 'CF-2025-047', shipment: 'COSCO456789', port: 'Long Beach',  status: 'inspection', clear: '-',       doc: 'complete', docLabel: 'Complete'     },
    { id: 'CF-2025-048', shipment: 'OOLU234567',  port: 'Seattle',     status: 'cleared',    clear: '2.1 hrs', doc: 'complete', docLabel: 'Complete'     },
  ],

};

// ─── Style Maps ───────────────────────────────────────────────────────────────

const ACCENT = {
  orange:  {
    iconBg:    'bg-orange-50  dark:bg-orange-900/20',
    iconText:  'text-orange-600 dark:text-orange-400',
    iconRing:  'ring-orange-100 dark:ring-orange-800/60',
    trendBg:   'bg-orange-50  dark:bg-orange-900/20',
    trendText: 'text-orange-700 dark:text-orange-400',
    bar:       'bg-orange-500',
    beam:      'rgba(249,115,22,0.72)',
  },
  emerald: {
    iconBg:    'bg-emerald-50  dark:bg-emerald-900/20',
    iconText:  'text-emerald-600 dark:text-emerald-400',
    iconRing:  'ring-emerald-100 dark:ring-emerald-800/60',
    trendBg:   'bg-emerald-50  dark:bg-emerald-900/20',
    trendText: 'text-emerald-700 dark:text-emerald-400',
    bar:       'bg-emerald-500',
    beam:      'rgba(16,185,129,0.72)',
  },
  blue:    {
    iconBg:    'bg-blue-50  dark:bg-blue-900/20',
    iconText:  'text-blue-600 dark:text-blue-400',
    iconRing:  'ring-blue-100 dark:ring-blue-800/60',
    trendBg:   'bg-blue-50  dark:bg-blue-900/20',
    trendText: 'text-blue-700 dark:text-blue-400',
    bar:       'bg-blue-500',
    beam:      'rgba(59,130,246,0.72)',
  },
  rose:    {
    iconBg:    'bg-rose-50  dark:bg-rose-900/20',
    iconText:  'text-rose-600 dark:text-rose-400',
    iconRing:  'ring-rose-100 dark:ring-rose-800/60',
    trendBg:   'bg-rose-50  dark:bg-rose-900/20',
    trendText: 'text-rose-700 dark:text-rose-400',
    bar:       'bg-rose-500',
    beam:      'rgba(244,63,94,0.72)',
  },
};

const TONE = {
  ink:     {
    iconBg:   'bg-ink-100  dark:bg-slate-800',
    iconText: 'text-ink-700  dark:text-slate-300',
    value:    'text-ink-900  dark:text-white',
  },
  orange:  {
    iconBg:   'bg-orange-50  dark:bg-orange-900/20',
    iconText: 'text-orange-600 dark:text-orange-400',
    value:    'text-orange-600 dark:text-orange-400',
  },
  emerald: {
    iconBg:   'bg-emerald-50  dark:bg-emerald-900/20',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    value:    'text-emerald-600 dark:text-emerald-400',
  },
  blue:    {
    iconBg:   'bg-blue-50  dark:bg-blue-900/20',
    iconText: 'text-blue-600 dark:text-blue-400',
    value:    'text-blue-700 dark:text-blue-400',
  },
};

const STATUS = {
  pending:    { label: 'Pending',    cls: 'bg-orange-50  dark:bg-orange-900/20  text-orange-700  dark:text-orange-400  ring-orange-100  dark:ring-orange-800/50'   },
  approved:   { label: 'Approved',   cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 ring-emerald-100 dark:ring-emerald-800/50'  },
  delayed:    { label: 'Delayed',    cls: 'bg-rose-50    dark:bg-rose-900/20    text-rose-700    dark:text-rose-400    ring-rose-100    dark:ring-rose-800/50'      },
  submitted:  { label: 'Submitted',  cls: 'bg-blue-50    dark:bg-blue-900/20    text-blue-700    dark:text-blue-400    ring-blue-100    dark:ring-blue-800/50'      },
  cleared:    { label: 'Cleared',    cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 ring-emerald-100 dark:ring-emerald-800/50'  },
  inspection: { label: 'Inspection', cls: 'bg-violet-50  dark:bg-violet-900/20  text-violet-700  dark:text-violet-400  ring-violet-100  dark:ring-violet-800/50'   },
};

const DOC_TONE = {
  complete: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 ring-emerald-100 dark:ring-emerald-800/50',
  missing:  'bg-rose-50    dark:bg-rose-900/20    text-rose-700    dark:text-rose-400    ring-rose-100    dark:ring-rose-800/50',
  review:   'bg-amber-50   dark:bg-amber-900/20   text-amber-700   dark:text-amber-400   ring-amber-100   dark:ring-amber-800/50',
  awaiting: 'bg-orange-50  dark:bg-orange-900/20  text-orange-700  dark:text-orange-400  ring-orange-100  dark:ring-orange-800/50',
};

const DOT_COLOR = {
  pending:    'bg-orange-500',
  approved:   'bg-emerald-500',
  delayed:    'bg-rose-500',
  submitted:  'bg-blue-500',
  cleared:    'bg-emerald-500',
  inspection: 'bg-violet-500',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ k }) {
  const Icon = ICON_MAP[k.iconKey];
  const a = ACCENT[k.accent];
  return (
    <div className="group relative overflow-hidden rounded-xl p-[1px]
                    transition-all duration-200 ease-out
                    hover:shadow-card hover:-translate-y-0.5">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-[-130%] animate-[spin_7s_linear_infinite]
                   bg-[conic-gradient(from_0deg,transparent_0deg,transparent_300deg,var(--beam-color)_330deg,transparent_360deg)]"
        style={{ '--beam-color': a.beam }}
      />
      {/* Card background fill — light: white, dark: slate-900 */}
      <span aria-hidden className="pointer-events-none absolute inset-[1px] rounded-[11px] bg-white dark:bg-slate-900" />
      <div className="relative z-10 rounded-[11px] border border-ink-100 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
        <span className={`absolute inset-x-0 top-0 h-[3px] ${a.bar} opacity-0 group-hover:opacity-100 transition-opacity`} />
        <div className="flex items-center justify-between">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ring-1 ${a.iconBg} ${a.iconText} ${a.iconRing}`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold ${a.trendBg} ${a.trendText}`}>
            <ArrowUpRight className={`w-3 h-3 ${k.trendUp ? '' : 'rotate-90'}`} strokeWidth={2.5} />
            {k.trend}
          </span>
        </div>
        <div className="mt-4 text-[28px] font-bold text-ink-900 dark:text-white tabular-nums leading-none tracking-tight">{k.value}</div>
        <div className="mt-1.5 text-[13px] font-semibold text-ink-800 dark:text-slate-200">{k.label}</div>
        <div className="mt-1 text-[11.5px] text-ink-500 dark:text-slate-400">{k.trend} vs last month · {k.sub}</div>
      </div>
    </div>
  );
}

function FilingsMarquee({ filings, recent }) {
  const items = [
    ...filings.map((f) => ({ id: f.id, port: f.port, status: f.status })),
    ...recent.map((r)  => ({ id: r.id, port: r.port, status: r.status })),
  ];
  const loop = [...items, ...items];
  const fade = 'linear-gradient(to right, transparent 0, black 56px, black calc(100% - 56px), transparent 100%)';

  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900
                        border border-ink-100 dark:border-slate-700
                        shadow-card dark:shadow-slate-900/40
                        overflow-hidden">
      <div className="flex items-stretch">
        <div className="shrink-0 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white
                        flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inset-0 rounded-full bg-white/70" />
            <span className="relative rounded-full h-2 w-2 bg-white" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Live Filings</span>
        </div>
        <div
          className="relative flex-1 overflow-hidden py-3"
          style={{ maskImage: fade, WebkitMaskImage: fade }}
        >
          <motion.div
            className="flex gap-8 whitespace-nowrap will-change-transform pl-6"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 50, ease: 'linear', repeat: Infinity }}
          >
            {loop.map((it, i) => (
              <span key={`${it.id}-${i}`} className="inline-flex items-center gap-2 text-[12.5px]">
                <span className={`w-1.5 h-1.5 rounded-full ${DOT_COLOR[it.status] || 'bg-ink-400'}`} />
                <span className="font-mono font-semibold text-ink-900 dark:text-white">{it.id}</span>
                <span className="text-ink-400 dark:text-slate-500">·</span>
                <span className="text-ink-700 dark:text-slate-300">{it.port}</span>
                <span className="text-ink-300 dark:text-slate-600">•</span>
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SummaryStats({ summary }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
      {summary.map((s) => {
        const Icon = ICON_MAP[s.iconKey];
        const t = TONE[s.tone];
        return (
          <div key={s.label}
               className="rounded-2xl bg-white dark:bg-slate-900
                          border border-ink-100 dark:border-slate-700
                          shadow-card dark:shadow-slate-900/40
                          px-5 py-4 flex items-center gap-4
                          transition-all duration-200 hover:shadow-cardHover hover:-translate-y-0.5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.iconBg} ${t.iconText}`}>
              <Icon className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 dark:text-slate-400">{s.label}</div>
              <div className={`mt-0.5 text-[22px] font-bold tabular-nums leading-none ${t.value}`}>{s.value}</div>
              <div className="mt-1 text-[11px] text-ink-500 dark:text-slate-500">{s.hint}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ClearanceChart({ countries }) {
  const max = 80;
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900
                        border border-ink-100 dark:border-slate-700
                        shadow-card dark:shadow-slate-900/40
                        overflow-hidden">
      <div className="px-5 py-4 border-b border-ink-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 text-white flex items-center justify-center">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="text-[14px] font-semibold text-ink-900 dark:text-white">
            Average Clearance Time by Country (Hours)
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 text-[11px] text-ink-600 dark:text-slate-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-indigo-500 to-cyan-400" />
          Clearance Time (Hours)
        </div>
        <div className="relative h-[260px] flex items-end gap-3 pl-10 pr-2">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-6 w-9 flex flex-col justify-between text-[10px] text-ink-400 dark:text-slate-500">
            {[80, 60, 40, 20, 0].map((v) => <span key={v}>{v}</span>)}
          </div>
          {/* Grid lines */}
          <div className="absolute left-9 right-2 top-0 bottom-6 flex flex-col justify-between">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="border-t border-ink-100 dark:border-slate-700/60" />
            ))}
          </div>
          {countries.map((c) => (
            <div key={c.name} className="relative z-10 flex-1 flex flex-col items-center justify-end h-full">
              <div className="relative w-full h-[calc(100%-1.5rem)] max-w-[26px] flex flex-col items-center justify-end group">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150
                                 absolute -top-7 text-[11px] font-semibold text-ink-900 dark:text-white">
                  {c.hours}h
                </span>
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-indigo-500 via-blue-500 to-cyan-400
                             hover:from-indigo-600 hover:via-blue-600 hover:to-cyan-500 transition-all duration-200
                             shadow-[0_-6px_20px_-4px_rgba(99,102,241,0.4)]"
                  style={{ height: `${(c.hours / max) * 100}%` }}
                />
              </div>
              <span className="mt-2 text-[10.5px] text-ink-600 dark:text-slate-400 text-center leading-tight">
                {c.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortFlag({ port }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-ink-700 dark:text-slate-300">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
      {port}
    </span>
  );
}

function RecentFilingsTable({ filings }) {
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900
                        border border-ink-100 dark:border-slate-700
                        shadow-card dark:shadow-slate-900/40
                        overflow-hidden">
      <div className="px-5 py-4 border-b border-ink-100 dark:border-slate-700
                      flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center">
            <ListChecks className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-ink-900 dark:text-white">Recent Customs Filings</div>
            <div className="text-[11.5px] text-ink-500 dark:text-slate-400">Container-level filings · live status</div>
          </div>
        </div>
        <button className="inline-flex items-center gap-2 h-9 px-3 rounded-lg
                           border border-ink-200 dark:border-slate-700
                           text-[12px] font-semibold text-ink-700 dark:text-slate-300
                           hover:bg-ink-100 dark:hover:bg-slate-800 transition-colors">
          View All
        </button>
      </div>
      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-[12.5px]">
          <thead className="sticky top-0 z-10
                            bg-ink-100/60 dark:bg-slate-800/70
                            backdrop-blur
                            text-[10.5px] uppercase tracking-[0.14em]
                            text-ink-500 dark:text-slate-400">
            <tr>
              <th className="text-left font-semibold px-5 py-3">Container ID</th>
              <th className="text-left font-semibold px-3 py-3">Port</th>
              <th className="text-left font-semibold px-3 py-3">Country</th>
              <th className="text-left font-semibold px-3 py-3">Filing Date</th>
              <th className="text-left font-semibold px-3 py-3">Documents</th>
              <th className="text-right font-semibold px-3 py-3">Clearance Time</th>
              <th className="text-left font-semibold px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filings.map((f, i) => {
              const s = STATUS[f.status];
              return (
                <tr key={f.id}
                    className={[
                      'border-t border-ink-100 dark:border-slate-700/50',
                      'transition-colors hover:bg-blue-50/30 dark:hover:bg-blue-900/10',
                      i % 2 === 1 ? 'bg-ink-100/20 dark:bg-slate-800/30' : '',
                    ].join(' ')}>
                  <td className="px-5 py-3 font-mono font-semibold text-ink-900 dark:text-white">{f.id}</td>
                  <td className="px-3 py-3"><PortFlag port={f.port} /></td>
                  <td className="px-3 py-3 text-ink-700 dark:text-slate-300">{f.country}</td>
                  <td className="px-3 py-3 text-ink-700 dark:text-slate-300 tabular-nums">{f.date}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2 h-6 rounded-md ring-1 text-[11px] font-semibold ${DOC_TONE[f.doc]}`}>
                      {f.docLabel}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink-700 dark:text-slate-300">{f.clear}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 h-6 rounded-full ring-1 text-[11px] font-semibold ${s.cls}`}>
                      {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RecentSummary({ recent }) {
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900
                        border border-ink-100 dark:border-slate-700
                        shadow-card dark:shadow-slate-900/40
                        overflow-hidden">
      <div className="px-5 py-4 border-b border-ink-100 dark:border-slate-700 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[14px] font-semibold text-ink-900 dark:text-white">Recent Filings — Summary</div>
          <div className="text-[11.5px] text-ink-500 dark:text-slate-400">High-level filing IDs and outcomes</div>
        </div>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-ink-100/60 dark:bg-slate-800/70
                            text-[10.5px] uppercase tracking-[0.14em]
                            text-ink-500 dark:text-slate-400">
            <tr>
              <th className="text-left font-semibold px-5 py-3">Filing ID</th>
              <th className="text-left font-semibold px-3 py-3">Shipment</th>
              <th className="text-left font-semibold px-3 py-3">Port</th>
              <th className="text-left font-semibold px-3 py-3">Status</th>
              <th className="text-right font-semibold px-3 py-3">Clearance Time</th>
              <th className="text-left font-semibold px-5 py-3">Documents</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r) => {
              const s = STATUS[r.status];
              return (
                <tr key={r.id}
                    className="border-t border-ink-100 dark:border-slate-700/50
                               hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors">
                  <td className="px-5 py-3">
                    <span className="text-blue-700 dark:text-blue-400 font-semibold font-mono">{r.id}</span>
                  </td>
                  <td className="px-3 py-3 font-mono text-ink-800 dark:text-slate-200">{r.shipment}</td>
                  <td className="px-3 py-3 text-ink-700 dark:text-slate-300">{r.port}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2.5 h-6 rounded-full ring-1 text-[11px] font-semibold uppercase tracking-wide ${s.cls}`}>
                      {s.label}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink-700 dark:text-slate-300">{r.clear}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 h-6 rounded-md ring-1 text-[11px] font-semibold ${DOC_TONE[r.doc]}`}>
                      {r.docLabel}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const CustomsFiling = () => {
  const { t } = useTranslation();
  const { summary } = useSelector((s) => s.booking);
  const fromRef = useRef(null);
  const toRef   = useRef(null);

  const [fromDate, setFromDate] = useState(getMonthsAgoForInput(3));
  const [toDate,   setToDate]   = useState(getTodayForInput());

  const formatDate = (dateString) => {
    if (!dateString) return 'Select date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Select date';
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return 'Select date';
    }
  };
const insightCards = useMemo(() => [
  {
    icon: AlertTriangle,
    title: 'Pending Clearance',
    value: summary?.total ?? 0,
    sub: 'awaiting clearance',
    trend: '+12%',
    trendUp: true,
    accentKey: 'orange',
  },
  {
    icon: CheckCircle2,
    title: 'Cleared Today',
    value: summary?.active ?? 0,
    sub: 'shipments cleared',
    trend: t('onTrack'),
    trendUp: true,
    accentKey: 'emerald',
  },
  {
    icon: Clock,
    title: 'Avg Processing Time',
    value: summary?.intransit ?? 0,
    sub: 'per filing',
    trend: '3 arriving',
    trendUp: true,
    accentKey: 'blue',
  },
  {
    icon: ShieldAlert,
    title: 'Issues / Delays',
    value: summary?.needattension ?? 0,
    sub: 'needs review',
    trend: t('reviewRequired'),
    trendUp: false,
    accentKey: 'rose',
  },
], [summary, t]);
  const headingText = 'Customs Filing & Compliance';

  // Destructure data — swap DASHBOARD_DATA with your API response here
  const { kpis, summary: summaryStats, countries, filings, recent } = DASHBOARD_DATA;

  return (
    <div className="p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="space-y-5"
      >

        {/* Live filings marquee */}
        <FilingsMarquee filings={filings} recent={recent} />
        {/* ── HEADER — unchanged ───────────────────────────────────────────── */}
        <div className="rounded-2xl overflow-hidden
                        border border-gray-200 dark:border-gray-800
                        bg-white dark:bg-gray-900
                        shadow-sm dark:shadow-gray-900/40">

          {/* Top bar */}
          <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center gap-4">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                            bg-gradient-to-br from-blue-600 to-indigo-600 text-white
                            flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">
                <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                Customs Module
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 8, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                className="mt-1.5 text-[22px] font-bold text-slate-900 dark:text-white tracking-tight leading-tight"
              >
                <motion.span
                  className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                  initial={{ width: '0ch' }}
                  animate={{ width: ['0ch', `${headingText.length}ch`, `${headingText.length}ch`, '0ch'] }}
                  transition={{ duration: 4.8, times: [0, 0.22, 0.9, 1], ease: ['linear', 'linear', 'easeInOut'], repeat: Infinity }}
                >
                  {headingText}
                </motion.span>
              </motion.h2>
              <div className="mt-1 text-[12.5px] text-slate-500 dark:text-slate-400">
                Real-time customs clearance tracking and compliance management.
              </div>
            </div>
              <button
              // onClick={onCreateNew}
              className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg
                         bg-gradient-to-br from-blue-600 to-indigo-600 text-white 
                         text-xs font-medium
                         transition-colors"
            >
              <FileText className="w-4 h-4" />
              <span className=" xs:inline">New Filling</span>

            </button>
            <div className="shrink-0 inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[12.5px] hover:border-slate-300 transition-colors">
              <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <div className="inline-flex items-center gap-1 cursor-pointer select-none" onClick={() => fromRef.current?.showPicker()}>
                <span className="text-slate-500 dark:text-slate-400">From</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">{formatDate(fromDate)}</span>
                <input ref={fromRef} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="sr-only" />
              </div>
              <span className="text-slate-300 dark:text-slate-600">—</span>
              <div className="inline-flex items-center gap-1 cursor-pointer select-none" onClick={() => toRef.current?.showPicker()}>
                <span className="text-slate-500 dark:text-slate-400">To</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">{formatDate(toDate)}</span>
                <input ref={toRef} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="sr-only" />
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
            </div>
          </div>

          {/* Compliance Insight strip */}
          <div className="px-6 pt-5">
            <div className="relative rounded-xl bg-gradient-to-r from-blue-50 to-blue-50/40 dark:from-blue-800/30 dark:to-slate-800/80 border border-blue-100 dark:border-slate-700 px-5 py-4 flex items-start gap-4 transition-all">
              <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r bg-gradient-to-b from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-400" />
              <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md dark:shadow-blue-900/20">
                <TriangleAlert className="w-4 h-4" strokeWidth={2.4} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-400">
                  Customs Compliance Insight
                </div>
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">
                  {summary?.summary || '13 containers awaiting customs at Rotterdam. Clearance delay at Mumbai Port increased by 14%. Recommend pre-submission of forms for high-risk shipments.'}
                </p>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="px-4 sm:px-5 pt-4 pb-3 grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
            {insightCards.map((card) => (
              <AIInsightCard key={card.title} {...card} />
            ))}
          </div>
        </div>
        {/* ── END HEADER ───────────────────────────────────────────────────── */}


        {/* Customs KPI cards (spinning beam style) */}
      

        {/* Summary stats + Clearance chart */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
          <SummaryStats summary={summaryStats} />
          <ClearanceChart countries={countries} />
        </div>

        {/* Full container filings table */}
        <RecentFilingsTable filings={filings} />

        {/* High-level filing IDs summary */}
        <RecentSummary recent={recent} />

        <div className="text-center text-[11px] text-ink-400 dark:text-slate-600 pt-2">
          v1.22.25 · shipez.ai enterprise TMS
        </div>

      </motion.div>
    </div>
  );
};

export default CustomsFiling;